import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import TopNav from './TopNav'; // assuming your imports
import InteractiveMap from './InteractiveMap';
import ReportPanel from './ReportPanel';
import ChatPanel from './ChatPanel';
import schema from './report_schema.json';
import analysis_schema from './analysis_schema.json';
import analysis_schema_per_parcel from './analysis_schema_per_parcel.json'
import { PREVIEW_DATA_LAYER_MAPS_ENABLED } from '../config/previewCapabilities';

const AcquisitionIntelInterface = () => {
  const location = useLocation();
  const [cursor, setCursor] = useState('blue');
  const [reportIsReady, setReportIsReady] = useState(false);
  const mapApiRef = useRef(null);
  const [wetlandToggle, setWetlandToggle] = useState('');
  const [elevationToggle, setElevationToggle] = useState('');
  const [soilToggle, setSoilToggle] = useState('');
  const [floodToggle, setFloodToggle] = useState('');
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [isReportVisible, setIsReportVisible] = useState(true);
  const heatmapTriggerRef = useRef(null);
  const [activeTab, setActiveTab] = useState('analysis');
  const [bluePolygons, setBluePolygons] = useState([]);
  // const [areaName, setAreaName] = useState("Lakewood Ranch");
  const [areaName, setAreaName] = useState("USA");
  const [parcelLabels, setParcelLabels] = useState([]);
  const removeMarkerAtIndexRef = useRef(null);
  const nextParcelNumber = useRef(1);
  const [liveAPNs, setLiveAPNs] = useState([]); // stores APNs as strings
  const [liveAddress, setLiveAddress] = useState([]);
  const [selectedApns, setSelectedApns] = useState([]);
  const [chatMessages, setChatMessages] = useState([
    { sender: 'system', message: 'Welcome! Ask me anything about your selected parcels.' },
    { sender: 'user', message: 'What are the flood risks for my selected parcel?' },
    { sender: 'system', message: 'The property at 0 University Pkwy is located in Flood Zone X, classified as an area of minimal flood hazard. It\'s outside the 100-year floodplain but may experience flooding during larger events like 500-year floods.' }
  ]);
  const [reportData, setReportData] = useState([]);
  const [analysisSchema, setAnalysisSchema] = useState([]);
  const [combined, setCombined] = useState([]);
  const [reportText, setReportText] = useState([]);
  const [reportAnalysis, setReportAnalysis] = useState([]);
  const [reportAddress, setReportAddress] = useState([]);
  const [reportAPN, setReportAPN] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [cursorDrawTrigger, setCursorDrawTrigger] = useState(0);
  const [searchText, setSearchText] = useState('');
  const [searchError, setSearchError] = useState("");
  const [parcelIds, setParcelIds] = useState([]);
  const [parcelBoundaries, setParcelBoundaries] = useState([]);
  const nextParcelIdRef = useRef(1);
  const [selectedParcelIndex, setSelectedParcelIndex] = useState(0);
  const [partialReports, setPartialReports] = useState({});
  const removeMarkersByAPNsRef = useRef(null);
  const [reportInProgress, setReportInProgress] = useState(false);
  const bluePolygonsRef = useRef(bluePolygons);
  const [reportProgress, setReportProgress] = useState(false);
  const [parcels, setParcels] = useState([]);
  const [creditNotice, setCreditNotice] = useState(null);
  const autoLaunchHandledRef = useRef(false);
  const autoLaunchAnalyzePendingRef = useRef(false);
  const autoLaunchAnalyzeStartedRef = useRef(false);
  useEffect(() => { bluePolygonsRef.current = bluePolygons; }, [bluePolygons]);

  const normalizeCoordinate = (value) => {
    if (value == null) return null;
    const trimmed = String(value).trim();
    if (!trimmed) return null;
    const numeric = Number(trimmed);
    return Number.isFinite(numeric) ? numeric : null;
  };

  const adjustCreditSummary = (creditType, nextValue = null) => {
    const field = creditType === "analysis" ? "analysis_credits" : "preview_credits";
    const unlimitedField = creditType === "analysis" ? "analysis_credits_unlimited" : "preview_credits_unlimited";
    setCreditSummary(prev => {
      if (prev?.[unlimitedField]) {
        return prev;
      }
      const currentValue = Number(prev?.[field] || 0);
      const updatedValue = nextValue == null
        ? Math.max(currentValue - 1, 0)
        : Math.max(Number(nextValue || 0), 0);
      return {
        ...prev,
        [field]: updatedValue,
      };
    });
  };


useEffect(() => {
  const params = new URLSearchParams(location.search);
  const queryLat = params.get('lat');
  const queryLon = params.get('lon');
  const queryApn = params.get('apn');
  const autoLaunch = params.get('auto_launch') === '1';

  if (!autoLaunch || autoLaunchHandledRef.current) {
    return;
  }

  let cancelled = false;

  async function bootstrapAutoLaunch() {
    try {
      const response = await fetch("/api/auth/status", {
        credentials: "include"
      });
      const data = await response.json();
      if (cancelled || !data?.authenticated) {
        return;
      }

      const ctx = data.auto_launch_context || {};
      const lat = normalizeCoordinate(queryLat ?? ctx.lat);
      const lon = normalizeCoordinate(queryLon ?? ctx.lon);
      if (lat == null || lon == null) {
        autoLaunchAnalyzePendingRef.current = false;
        return;
      }

      const latlonSearch = `${lat},${lon}`;
      clearSelectionState();
      setSearchText(latlonSearch);

      const geocodeResponse = await fetch(`/api/geocode?area_name=${encodeURIComponent(latlonSearch)}`, {
        credentials: "include"
      });
      const geocodeData = await geocodeResponse.json();
      if (!geocodeResponse.ok || !geocodeData?.centroid) {
        throw new Error(geocodeData?.error || "Failed to load parcel from launch coordinates");
      }

      const parcelApn = geocodeData.apn ? String(geocodeData.apn) : null;
      const parcelAddress = geocodeData.address || "";
      const parcelBoundary = geocodeData.boundary || [];
      const centroid = geocodeData.centroid;

      setBluePolygons([centroid]);
      setParcelBoundaries(parcelBoundary ? [parcelBoundary] : []);
      setSelectedApns(parcelApn ? [parcelApn] : []);
      setLiveAPNs(parcelApn ? [parcelApn] : []);
      setLiveAddress(parcelAddress ? [parcelAddress] : []);
      setParcels(parcelApn || parcelAddress ? [{
        apn: parcelApn || "",
        address: parcelAddress || "",
      }] : []);

      if (parcelApn && parcelAddress && parcelBoundary) {
        window.dispatchEvent(new CustomEvent("parcelAdded", {
          detail: { apn: parcelApn, address: parcelAddress, boundary: parcelBoundary }
        }));
      }
      window.dispatchEvent(new CustomEvent("panToCoords", {
        detail: { lat, lon }
      }));

      autoLaunchHandledRef.current = true;
      autoLaunchAnalyzePendingRef.current = true;
    } catch (err) {
      console.error("Failed to bootstrap Landfinder auto-launch:", err);
    }
  }

  bootstrapAutoLaunch();
  return () => {
    cancelled = true;
  };
}, [location.search]);


useEffect(() => {
  const handler = (e) => {
    const { apn, address, boundary } = e.detail;

    setLiveAPNs(prev => {
      const apns = prev.filter(a => a !== "All Parcels Combined");
      // Avoid duplicates
      const newAPNs = apns.includes(apn) ? apns : [...apns, apn];
      return [...newAPNs];
    });

    setLiveAddress(prev => {
      const addresses = prev.filter(a => a !== "All Parcels Combined");
      const newAddresses = addresses.includes(address) ? addresses : [...addresses, address];
      return [...newAddresses];
    });

    // setParcelBoundaries(prev => [...prev, boundary]);
    setParcelBoundaries(prev => {
      // if (prev.length === 0) return [boundary];
      const exists = prev.some(b => JSON.stringify(b) === JSON.stringify(boundary));
      return exists ? prev : [...prev, boundary];
    });
    console.log('setParcelBoundaries', setParcelBoundaries)
  };

  window.addEventListener("parcelAdded", handler);
  return () => window.removeEventListener("parcelAdded", handler);
}, []);

useEffect(() => {
  const handler = (e) => setSelectedParcelIndex(e.detail);
  window.addEventListener('parcelSelectionChanged', handler);
  return () => window.removeEventListener('parcelSelectionChanged', handler);
}, []);

  const handleCursorChange = (newCursor) => {
    setCursor(newCursor);
    setCursorDrawTrigger(prev => prev + 1);
  };

  const resetReportState = () => {
      console.log("liveAPNs", liveAPNs)
      if (removeMarkersByAPNsRef.current && liveAPNs.length > 0) {
        console.log("Calling removeMarkersByAPNs with:", liveAPNs);
    removeMarkersByAPNsRef.current(liveAPNs);
  }
  setBluePolygons([]);
  setReportData([]);
  setAnalysisSchema([]);
  setCombined([]);
  setReportText([]);
  setReportAnalysis([]);
  setReportAddress([]);
  setReportAPN([]);
  setSelectedParcelIndex(0);
  setPartialReports({});
  setParcelLabels([]);
  setLiveAPNs([]);
  setSelectedApns([]);
  setLiveAddress([]);
  setParcels([]);
  setParcelBoundaries([]);
  setWetlandToggle(null);
  setElevationToggle(null);
  setSoilToggle(null);
  setReportInProgress(false);
  setReportProgress(false);
  nextParcelNumber.current = 1;
};

  const clearSelectionState = () => {
      setBluePolygons([]);
      setReportData([]);
      setAnalysisSchema([]);
      setCombined([]);
      setReportText([]);
      setReportAnalysis([]);
      setReportAddress([]);
      setReportAPN([]);
      setSelectedParcelIndex(0);
      setPartialReports({});
      setParcelBoundaries([]);
      setParcelLabels([]);
      setLiveAPNs([]);
      setSelectedApns([]);
      setLiveAddress([]);
      setParcels([]);
      setWetlandToggle(null);
      setElevationToggle(null);
      setSoilToggle(null);
      nextParcelNumber.current = 1;
      setReportInProgress(false);
      setReportProgress(false);
  };

  const submitSearch = (value) => {
      const normalizedValue = String(value || '').trim();
      if (!normalizedValue) return;

      setSearchError("");
      setSearchText(normalizedValue);
      setAreaName(normalizedValue);
      clearSelectionState();
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      submitSearch(searchText);
    }
  };

  const clearOldReportOnly = () => {
  setReportData([]);
  setAnalysisSchema([]);
  setCombined([]);
  setReportText([]);
  setReportAnalysis([]);
  setReportAddress([]);
  setReportAPN([]);
  setPartialReports({});
  setParcelLabels([]);
  setSelectedParcelIndex(0);
  setWetlandToggle(null);
  setElevationToggle(null);
  setSoilToggle(null);
};

 const generateReport = async () => {
  // resetReportState();
  // clearOldReportOnly();
  if (!bluePolygons || bluePolygons.length === 0) {
    alert("Click at least one location before starting a preview.");
    return;
  }

  window.setIsGeneratingReport(true);
  setReportInProgress(true); 
  setReportProgress(true); 

  const payload = {
    coords: bluePolygons,
    schema
  };

  // Step 1: POST to prepare data (you may need to adjust backend for this)
  const prepResponse = await fetch('/api/generate_report', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });

  if (!prepResponse.ok) {
    let creditPayload = null;
    try {
      creditPayload = await prepResponse.json();
    } catch (e) {
      creditPayload = null;
    }
    if (prepResponse.status === 402 && creditPayload?.error === "insufficient_credits") {
      adjustCreditSummary(creditPayload.credit_type, creditPayload.remaining_credits);
      setCreditNotice(creditPayload);
    }
    window.setIsGeneratingReport(false);
    setReportInProgress(false);
    setReportProgress(false);
    return;
  }

  adjustCreditSummary("preview");

  // Step 2: Stream via SSE
  const source = new EventSource('/api/generate_report');

  source.onmessage = (event) => {
    if (!event.data) return;

    try {
      const parsed = JSON.parse(event.data);
      const { type, res } = parsed;

      if (type === 'error' || parsed.error) {
        const loader = document.getElementById("loader");
        if (loader) {
          loader.innerText = typeof res === 'string'
            ? res
            : 'Preview could not be completed. Please try again.';
        }
        window.setIsGeneratingReport(false);
        setReportInProgress(false);
        setReportProgress(false);
        source.close();
        return;
      } else if (type === 'progress') {
        console.log('progressprogressprogressprogressprogressprogressprogress');
        const loader = document.getElementById("loader");
        if (loader) {
          loader.innerText = res; // overwrite with latest
        }
      } else if (type === 'preview') {
        console.log('📦 Received preview');
        const { apn, section, address, acreage, zoning } = res;
        const apnStr = String(apn);
        console.log('  APN:', apn);
        console.log('  Section:', section);
        console.log('  address:', address);
        console.log('  acreage:', acreage);
        console.log('  zoning:', zoning);

        setPartialReports(prev => ({
          ...prev,
          [apnStr]: {
            ...(prev[apnStr] || {}),
            [section]: {
              ...(prev[apnStr]?.[section] || {}),
              ...(address && { address }),
              ...(acreage && { acreage }),
              ...(zoning && { zoning })
            }
          }
        }));

        setLiveAPNs(prev => prev.includes(apnStr) ? prev : [...prev, apnStr]);
        setLiveAddress(prev => (prev.includes(address) ? prev : [...prev, address]));
        setParcels(prev => {
          console.log("[auto-launch-debug] incoming preview merge", {
            incomingApn: apnStr,
            incomingAddress: address,
            incomingAcreage: acreage,
            incomingZoning: zoning,
            existingParcels: prev,
          });
          const existingIndex = prev.findIndex(p => p.apn === apnStr);
          if (existingIndex >= 0) {
            const nextParcels = prev.map((parcel, index) => {
              if (index !== existingIndex) return parcel;
              return {
                ...parcel,
                ...(address ? { address } : {}),
                ...(acreage ? { acreage } : {}),
                ...(zoning ? { zoning } : {}),
              };
            });
            console.log("[auto-launch-debug] updated existing parcel", {
              existingIndex,
              updatedParcel: nextParcels[existingIndex],
            });
            return nextParcels;
          }
          const nextParcels = [...prev, { apn: apnStr, address, acreage, zoning }];
          console.log("[auto-launch-debug] appended new parcel", {
            appendedParcel: nextParcels[nextParcels.length - 1],
          });
          return nextParcels;
        });
      }
        
      else if (type === 'partial_output') {
        console.log('📦 Received partial_output');
        const { apn, section, report, report_schema } = res;
        const apnStr = String(apn);

        console.log('  APN:', apn);
        console.log('  Section:', section);
        console.log('  Report:', report);
        console.log('  Report Schema:', report_schema);

        setPartialReports(prev => ({
          ...prev,
          [apnStr]: {
            ...(prev[apnStr] || {}),
            [section]: {
              ...(prev[apnStr]?.[section] || {}),
              ...(report && { report }),
              ...(report_schema && { report_schema }),
            }
          }
        }));

        setLiveAPNs(prev => prev.includes(apnStr) ? prev : [...prev, apnStr]);
      } else if (type === 'parcel_preview') {
        const apns = res.map(parcel => String(parcel.apn));
        const addresses = res.map(parcel => parcel.address);

        // Add new items without resurrecting removed ones by overwriting state
        setLiveAPNs(prev => {
          const s = new Set(prev);
          apns.forEach(a => s.add(a));
          return Array.from(s);
        });
        setLiveAddress(prev => {
          const s = new Set(prev);
          addresses.forEach(a => s.add(a));
          return Array.from(s);
        });

        // IMPORTANT: use the latest polygons, not the stale closure
        fetchParcelBoundaries(bluePolygonsRef.current);
      }

      else if (type === 'output') {
        setReportIsReady(true);
        setReportProgress(false); 
        // setReportInProgress(false);
        const loader = document.getElementById("loader");
        if (loader) {
          loader.innerText = '✅ Preview complete'; // overwrite with latest
        }
        const data = res;
        const apnLabels = data.apn.map((apn) => `Parcel ${apn}`);
        setParcelLabels(apnLabels);

        setReportData(data.schema);
        setReportText(data.report_str);
        setReportAddress(data.address);
        setReportAPN(data.apn);

        source.close();
        window.setIsGeneratingReport(false);
      }
    } catch (e) {
      console.warn('❌ Failed to parse SSE message', e);
    }
  };

  source.onerror = (err) => {
    console.error('SSE connection error:', err);
    window.setIsGeneratingReport(false);
    setReportProgress(false); 
    // setReportInProgress(false);
    source.close();
  };
};

const fetchParcelBoundaries = async (coords) => {
  const boundaries = await Promise.all(
    coords.map(([lon, lat]) =>
      fetch(`/api/fetch_parcel/${lat}/${lon}`).then((res) => res.json())
    )
  );
  const polygons = boundaries.map((b) => b.boundary);
  setParcelBoundaries(polygons);
};

  const handleDeleteParcel = (indexToRemove, apnToRemove) => {
    mapApiRef.current?.clearHeatmapOverlay?.();
    setWetlandToggle(null);
    setElevationToggle(null);
    setSoilToggle(null);
    window.setIsGeneratingHeatmaps?.(false);
    // if (removeMarkerAtIndexRef.current) {
    //   removeMarkerAtIndexRef.current(apnToRemove);
    // }
    setReportData(prev => prev.filter((_, i) => i !== indexToRemove));
    setReportText(prev => prev.filter((_, i) => i !== indexToRemove));
    setReportAnalysis(prev => prev.filter((_, i) => i !== indexToRemove));
    setAnalysisSchema(prev => prev.filter((_, i) => i !== indexToRemove));
    setCombined(prev => prev.filter((_, i) => i !== indexToRemove));
    setReportAddress(prev => prev.filter((_, i) => i !== indexToRemove));
    setReportAPN(prev => prev.filter((_, i) => i !== indexToRemove));
    setBluePolygons(prev => prev.filter((_, i) => i !== indexToRemove));
    setParcelLabels(prev => prev.filter((_, i) => i !== indexToRemove));
    setParcelBoundaries(prev => prev.filter((_, i) => i !== indexToRemove));
    setLiveAPNs(prev => prev.filter((_, i) => i !== indexToRemove));
    setLiveAddress(prev => prev.filter((_, i) => i !== indexToRemove));
    setParcels(prev => prev.filter((_, i) => i !== indexToRemove));
    setSelectedApns(prev => prev.filter(a => a !== apnToRemove));

    // // DONT KNOW
      setSelectedParcelIndex(prev => {
    if (prev > indexToRemove) {
      return prev - 1;        // shift left if after deleted item
    } else if (prev === indexToRemove) {
      return 0;               // reset to first parcel if the deleted one was selected
    } else {
      return prev;            // keep same if before deleted item
    }
  });

  };

  const handleSendMessage = () => {
    if (newMessage.trim() === '') return;

    setChatMessages([...chatMessages, { sender: 'user', message: newMessage }]);
    setNewMessage('');

    // Simulate response
    setTimeout(() => {
      setChatMessages(prev => [...prev, {
        sender: 'system',
        message: 'Based on the analysis of the selected parcel at 0 University Pkwy, Sarasota, I can provide you with details from the due diligence report. Is there a specific aspect you\'d like me to elaborate on?'
      }]);
    }, 1000);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') handleSendMessage();
  };

  useEffect(() => {
    const handler = (e) => setSelectedParcelIndex(e.detail);
    window.addEventListener('parcelSelectionChanged', handler);
    return () => window.removeEventListener('parcelSelectionChanged', handler);
  }, []);

useEffect(() => {
  setWetlandToggle(null);
  setElevationToggle(null);
  setSoilToggle(null);
}, [selectedParcelIndex]);

useEffect(() => {
  console.log("reportInProgress changed:", reportInProgress);
}, [reportInProgress]);

useEffect(() => {
  if (!autoLaunchAnalyzePendingRef.current || autoLaunchAnalyzeStartedRef.current) {
    return;
  }
  if (!Array.isArray(bluePolygons) || bluePolygons.length === 0) {
    return;
  }

  autoLaunchAnalyzeStartedRef.current = true;
  autoLaunchAnalyzePendingRef.current = false;
  generateReport();
}, [bluePolygons]);

// ADD this effect (place near your other window event listeners)
useEffect(() => {
  const handler = (e) => {
    const { index } = e.detail;
    if (index == null) return;
    // Use your existing delete pipeline so everything stays in sync:
    handleDeleteParcel(index, liveAPNs[index]);
    const apnToRemove = liveAPNs[index];
    setSelectedApns(prev => prev.filter(a => a !== apnToRemove));
  };
  window.addEventListener("parcelRemoved", handler);
  return () => window.removeEventListener("parcelRemoved", handler);
}, [liveAPNs]);

  const [userName, setUserName] = useState("");
  const [creditSummary, setCreditSummary] = useState({
    preview_credits: 0,
    analysis_credits: 0,
    preview_credits_unlimited: false,
    analysis_credits_unlimited: false,
    plan: "native",
    portfolio_enabled: false,
    portfolio_organization_id: null,
  });

  useEffect(() => {
    fetch("/auth/current-user", { credentials: "include" })
      .then(res => res.json())
      .then(data => {
        setUserName(data.username);
        setCreditSummary({
          preview_credits: Number(data.preview_credits || 0),
          analysis_credits: Number(data.analysis_credits || 0),
          preview_credits_unlimited: Boolean(data.preview_credits_unlimited),
          analysis_credits_unlimited: Boolean(data.analysis_credits_unlimited),
          plan: data.plan || "native",
          portfolio_enabled: Boolean(data.portfolio_enabled),
          portfolio_organization_id: data.portfolio_organization_id || null,
        });
      })
      .catch(err => console.error("Failed to fetch username:", err));
  }, []);

  return (
    <div className="flex flex-col h-screen bg-gray-100 relative">
      <TopNav
        cursor={cursor}
        setCursor={handleCursorChange}
        onGenerateReport={generateReport}
        toggleChat={() => setIsChatOpen(prev => !prev)}
        searchText={searchText}
        setSearchText={(value) => {
          setSearchError("");
          setSearchText(value);
        }}
        searchError={searchError}
        handleKeyDown={handleKeyDown}
        userName={userName}
        creditSummary={creditSummary}
        portfolioEnabled={creditSummary.portfolio_enabled}
      />

      {creditNotice && (
        <div className="absolute inset-0 z-[3000] flex items-center justify-center bg-slate-950/45 px-4">
          <div className="w-full max-w-md rounded-2xl border border-blue-100 bg-white shadow-2xl">
            <div className="border-b border-blue-50 bg-gradient-to-r from-blue-600 to-sky-500 px-6 py-4 text-white">
              <div className="text-lg font-semibold">Not Enough Credits</div>
              <div className="mt-1 text-sm text-blue-100">
                This action needs an available {creditNotice.credit_type || "analysis"} credit.
              </div>
            </div>
            <div className="px-6 py-5 text-sm text-slate-700">
              <p>{creditNotice.message || "You do not have enough credits to continue. Please contact your admin or support team."}</p>
              <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
                <div className="text-xs font-medium uppercase tracking-wide text-slate-500">Remaining Credits</div>
                <div className="mt-1 text-2xl font-semibold text-slate-900">
                  {Number.isFinite(Number(creditNotice.remaining_credits))
                    ? Number(creditNotice.remaining_credits)
                    : 0}
                </div>
              </div>
            </div>
            <div className="flex justify-end px-6 pb-5">
              <button
                onClick={() => setCreditNotice(null)}
                className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-blue-700"
              >
                Got it
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="flex flex-1 flex-col overflow-y-auto lg:flex-row lg:overflow-hidden">
        <div className={`${isReportVisible ? 'w-full lg:w-1/2' : 'flex-1'} relative min-h-[45vh] lg:min-h-0`}>
          <InteractiveMap
            areaName={areaName}
            cursor={cursor}
            cursorDrawTrigger={cursorDrawTrigger}
            bluePolygons={bluePolygons}
            setBluePolygons={setBluePolygons}
            setRemoveMarkerAtIndexRef={(fn) => (removeMarkerAtIndexRef.current = fn)}
            parcelBoundaries={parcelBoundaries}
            selectedParcelIndex={selectedParcelIndex}
            setRemoveMarkersByAPNsRef={fn => (removeMarkersByAPNsRef.current = fn)}
            onGenerateReport={generateReport} 
            mapApiRef={mapApiRef}
            // heatmapType={activeTab === 'elevation' ? elevationToggle : wetlandToggle}
            heatmapType={PREVIEW_DATA_LAYER_MAPS_ENABLED ? (
              activeTab === 'elevation' 
              ? elevationToggle
              : activeTab === 'soil' 
                ? `Soil_${soilToggle}`   // Prefix Soil_ for soil tab
                : activeTab === 'flood'
                ? floodToggle
                :wetlandToggle
            ) : ''}
            reportInProgress={reportInProgress} 
            reportProgress={reportProgress} 
            liveAPNs={liveAPNs}
            setLiveAPNs={setLiveAPNs}
            selectedApns={selectedApns}           
            setSelectedApns={setSelectedApns}
            onSearchError={setSearchError}
          />
        </div>

        {isReportVisible && (
            <ReportPanel
              activeTab={activeTab}
              setActiveTab={setActiveTab}
              onClose={() => setIsReportVisible(false)}
              reportData={reportData}
              reportText={reportText}
              reportAddress={reportAddress}
              reportAPN={reportAPN}
              handleDeleteParcel={handleDeleteParcel}
              parcelLabels={parcelLabels}
              reportAnalysis={reportAnalysis}
              analysisSchema={analysisSchema}
              combined={combined}
              partialReports={partialReports}
              liveAPNs={liveAPNs}
              liveAddress={liveAddress}
              wetlandToggle={wetlandToggle}
              setWetlandToggle={setWetlandToggle}
              elevationToggle = {elevationToggle}
              setElevationToggle={setElevationToggle}
              soilToggle={soilToggle}
              setSoilToggle={setSoilToggle}
              floodToggle={floodToggle}
              setFloodToggle={setFloodToggle}
              mapApiRef={mapApiRef}
              reportInProgress={reportInProgress}
              onDismissWidgets={() => {
                setReportInProgress(false);
                setReportProgress(false);
              }}
              onGenerateReport={generateReport}
              bluePolygons={bluePolygons}
              parcels={parcels} 
              reportProgress={reportProgress} 
              selectedApns={selectedApns}
              selectedParcelIndex={selectedParcelIndex}
              setSelectedParcelIndex={setSelectedParcelIndex}
              onCreditError={setCreditNotice}
              onCreditConsumed={adjustCreditSummary}
              previewDataLayerMapsEnabled={PREVIEW_DATA_LAYER_MAPS_ENABLED}

            />
        )}

        {isChatOpen && (
          <ChatPanel
            chatMessages={chatMessages}
            newMessage={newMessage}
            setNewMessage={setNewMessage}
            onSend={handleSendMessage}
            onClose={() => setIsChatOpen(false)}
            onKeyPress={handleKeyPress}
          />
        )}

      </div>
    </div>
  );
};

export default AcquisitionIntelInterface;
